import { Grid } from '@mui/material';
import { Form, Formio } from 'formiojs';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import { applyCustomFormioConfig } from '~/components/Forms/CustomBuilder';
import { formsCss } from '~/components/Forms/formCss';
import { useBootstrapJs } from '~/hooks/forms';
import { FormioFormBuilderConfig } from '~/types/forms';
import { browserLogError } from '~/utils/helper';

import { FormTemplateSelections } from './components/FormTemplateSelections';
import { TemplateUpdateButtons } from './components/TemplateUpdateButtons';
import { overlappingEmplFormSubmit } from './data';
import {
  OverlappingEmplForm,
  useFormsOverlappingEmp,
} from './useFormsOverlappingEmp';
import { useStyles } from './useStyles';
declare module 'formiojs';

Formio.setBaseUrl(`${globalThis.location.origin}/api/forms`);

applyCustomFormioConfig();

export const OverlappingEmployment = () => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [formRootEl, setFormRootEl] = useState<HTMLElement>();

  const {
    employeeId,
    formTemplates,
    selectedTemplateId,
    formConfigData,
    latestVersion,
    latestVersionLoading,
    formTranslations,
    existingData,
    isDataLoading,
    workflowConfig,
    setSelectedTemplateId,
    refetchTemplateData,
  } = useFormsOverlappingEmp();

  const onVersionUpdated = (data: FormioFormBuilderConfig) => {
    const components = data?.components || [];
    data.components = components.slice(0, -3);
    if (refetchTemplateData) {
      refetchTemplateData();
    }
  };

  useBootstrapJs();

  useEffect(() => {
    let overlappingEmplForm: Form;

    if (
      formRootEl &&
      formConfigData &&
      selectedTemplateId &&
      formTranslations &&
      employeeId &&
      workflowConfig &&
      !isDataLoading
    ) {
      overlappingEmplForm = new Form(formRootEl, formConfigData, {
        ...formTranslations,
        language: formTranslations?.language,
      });

      const initializeForm = async () => {
        try {
          const inst = await overlappingEmplForm.ready;

          inst.submission = { data: { ...existingData, workflowConfig } };
          inst.triggerChange();

          inst.on('cancel', () => inst.cancel());
          inst.on('goToPreviousPage', () => inst.prevPage());
          inst.on('goToNextPage', () => inst.nextPage());

          inst.on('customSubmit', async (data: OverlappingEmplForm) => {
            try {
              await inst.submit();

              const resp = await overlappingEmplFormSubmit(selectedTemplateId, {
                data,
              });

              enqueueSnackbar(t('laOverlappingSuccess'), {
                variant: 'success',
              });
              if (resp) {
                history.push(`/portal/employee/${employeeId}/people`);
                window.location.reload();
              }
            } catch (err) {
              enqueueSnackbar(t('laOverlappingError'), { variant: 'error' });
              browserLogError('Error on overlapping employment submit:', err);
            }
          });
        } catch (err) {
          browserLogError('Form initialization error:', err);
        }
      };

      initializeForm();

      overlappingEmplForm.on(
        'error',
        (errors: Error) => {
          browserLogError('Form errors:', errors);
        },
        false,
      );
    }

    return () => {
      if (overlappingEmplForm) {
        overlappingEmplForm.off('submit');
      }
    };
  }, [
    formRef,
    formConfigData,
    selectedTemplateId,
    existingData,
    formRootEl,
    formTranslations,
    employeeId,
    history,
    isDataLoading,
    workflowConfig,
    t,
    enqueueSnackbar,
  ]);

  const rootElCallbackRef = useCallback((node) => {
    if (node) {
      setFormRootEl(node);
    }
  }, []);

  return (
    <>
      <style>{formsCss}</style>
      <Grid container direction="column" style={styles.formPageContainer}>
        <h2>{t('laAddNewEmployment')}</h2>
        <Grid item md={8} lg={6} style={styles.componentsContainer}>
          <Grid
            className="container-fluid"
            style={{ ...styles.container, maxWidth: '830px', marginLeft: 0 }}
          >
            <Grid item style={styles.container}>
              <FormTemplateSelections
                selectedTemplateId={selectedTemplateId ?? ''}
                formTemplates={formTemplates ?? []}
                onFormTemplateChange={setSelectedTemplateId}
              />
              <Link
                to={`/portal/employee/${employeeId}/people/basic?mode=overlapping-employment`}
                className="badge badge-info"
                style={styles.backToStandard}
              >
                {t('laBackToStandardOverlappingEmploymentForm')}
              </Link>
            </Grid>
            <TemplateUpdateButtons
              updated={
                formConfigData?.settings?.version === latestVersion?.version
              }
              formId={formConfigData?._id || ''}
              disableAction={false}
              loading={latestVersionLoading}
              onVersionUpdated={onVersionUpdated}
            />
          </Grid>
        </Grid>
        <Grid item md={8} lg={6} style={styles.componentsContainer}>
          <Grid className="container-fluid" style={styles.container}>
            <div ref={rootElCallbackRef} />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
